import { Attachment } from '@medplum/fhirtypes';
import { VOICE_RECORDING_PAYLOAD_ID } from './constants';

export interface VoiceRecordingSegment {
  payloadId: string;
  recordingSid: string;
  attachment: Attachment;
}

/** Dial and conference recordings are short fragments; call-session recording spans the full call. */
export function isFragmentedRecordingSource(recordingSource: string | undefined): boolean {
  if (!recordingSource) {
    return false;
  }
  const normalized = recordingSource.toLowerCase();
  return normalized === 'dialverb' || normalized === 'conference';
}

export function isVoiceRecordingPayloadId(id: string | undefined): boolean {
  if (!id) {
    return false;
  }
  return id === VOICE_RECORDING_PAYLOAD_ID || id.startsWith(`${VOICE_RECORDING_PAYLOAD_ID}-`);
}

export function isMergedVoiceRecordingPayloadId(id: string | undefined, title?: string): boolean {
  return id === VOICE_RECORDING_PAYLOAD_ID && parseRecordingStatusFromPayloadTitle(title) === 'merged';
}

export function isLegacyVoiceRecordingSegmentPayload(id: string | undefined, title?: string): boolean {
  return id === VOICE_RECORDING_PAYLOAD_ID && !isMergedVoiceRecordingPayloadId(id, title);
}

export function isVoiceRecordingSegmentPayloadId(id: string | undefined): boolean {
  if (!id) {
    return false;
  }
  return id.startsWith(`${VOICE_RECORDING_PAYLOAD_ID}-`);
}

export function parseRecordingStatusFromPayloadTitle(title: string | undefined): string | undefined {
  return title?.match(/Call Recording - ([^\s(]+)/)?.[1];
}

export function parseMergedSegmentSidsFromPayloadTitle(title: string | undefined): string[] {
  const match = title?.match(/segments=([^\s\]]+)/)?.[1];
  if (!match) {
    return [];
  }
  return match.split(',').filter(Boolean);
}

export function buildMergedVoiceRecordingPayloadTitle(params: {
  durationSeconds: number;
  segmentCount: number;
  recordingStartTime?: string;
  segmentSids: string[];
}): string {
  const started = normalizeRecordingStartTime(params.recordingStartTime);
  const startedSuffix = started ? ` started=${started}` : '';
  const segmentSuffix = params.segmentSids.length ? ` segments=${params.segmentSids.join(',')}` : '';
  return `Call Recording - merged (${params.durationSeconds}s) from ${params.segmentCount} segments${startedSuffix}${segmentSuffix}`;
}

export function parseRecordingSidFromTwilioUrl(url: string | undefined): string | undefined {
  return url?.match(/\/Recordings\/(RE[a-f0-9]+)/i)?.[1];
}

export function collectVoiceRecordingSegments(
  payload: Array<{ id?: string; contentAttachment?: Attachment }> | undefined
): VoiceRecordingSegment[] {
  const segments: VoiceRecordingSegment[] = [];
  const seenRecordingSids = new Set<string>();

  for (const item of payload ?? []) {
    const attachment = item.contentAttachment;
    if (!attachment?.url) {
      continue;
    }

    if (isVoiceRecordingSegmentPayloadId(item.id)) {
      const recordingSid = item.id!.slice(`${VOICE_RECORDING_PAYLOAD_ID}-`.length);
      if (!recordingSid || seenRecordingSids.has(recordingSid)) {
        continue;
      }
      seenRecordingSids.add(recordingSid);
      segments.push({ payloadId: item.id!, recordingSid, attachment });
      continue;
    }

    if (isLegacyVoiceRecordingSegmentPayload(item.id, attachment.title)) {
      const recordingSid = parseRecordingSidFromTwilioUrl(attachment.url);
      if (!recordingSid || seenRecordingSids.has(recordingSid)) {
        continue;
      }
      seenRecordingSids.add(recordingSid);
      segments.push({ payloadId: item.id!, recordingSid, attachment });
    }
  }

  return segments;
}

function sortRecordingAttachments(attachments: Attachment[]): Attachment[] {
  return [...attachments].sort((left, right) => {
    const startLeft = parseRecordingStartTimeFromPayloadTitle(left.title);
    const startRight = parseRecordingStartTimeFromPayloadTitle(right.title);
    if (startLeft !== startRight) {
      return startLeft - startRight;
    }
    return (left.creation ?? '').localeCompare(right.creation ?? '');
  });
}

export function selectRecordingAttachmentsForTimeline(
  payload: Array<{ id?: string; contentAttachment?: Attachment }> | undefined
): Attachment[] {
  const recordingItems = (payload ?? []).filter((item) => isVoiceRecordingPayloadId(item.id));
  const merged = recordingItems.find((item) => isMergedVoiceRecordingPayloadId(item.id, item.contentAttachment?.title));

  if (merged?.contentAttachment?.url) {
    return [merged.contentAttachment];
  }

  const segments = collectVoiceRecordingSegments(payload);
  if (segments.length > 0) {
    return sortRecordingAttachments(segments.map((segment) => segment.attachment));
  }

  return [];
}

export function shouldMergeVoiceRecordingSegments(params: {
  segmentRecordingSids: string[];
  segmentTitles: Array<string | undefined>;
  mergedPayloadTitle?: string;
}): boolean {
  const { segmentRecordingSids, segmentTitles, mergedPayloadTitle } = params;
  if (segmentRecordingSids.length < 2) {
    return false;
  }

  const allSegmentsCompleted = segmentTitles.every((title) => {
    const status = parseRecordingStatusFromPayloadTitle(title);
    return status === 'completed' || status === undefined;
  });
  if (!allSegmentsCompleted) {
    return false;
  }

  const existingMergedSids = parseMergedSegmentSidsFromPayloadTitle(mergedPayloadTitle);
  if (existingMergedSids.length > 0) {
    const expectedSids = [...segmentRecordingSids].sort().join(',');
    const mergedSids = [...existingMergedSids].sort().join(',');
    if (expectedSids === mergedSids) {
      return false;
    }
  }

  return true;
}

export function voiceRecordingPayloadId(recordingSid: string): string {
  return `${VOICE_RECORDING_PAYLOAD_ID}-${recordingSid}`;
}

export function normalizeRecordingStartTime(recordingStartTime: string | undefined): string | undefined {
  if (!recordingStartTime) {
    return undefined;
  }
  const parsed = new Date(recordingStartTime);
  if (Number.isNaN(parsed.getTime())) {
    return recordingStartTime;
  }
  return parsed.toISOString();
}

export function buildVoiceRecordingPayloadTitle(params: {
  recordingStatus: string;
  durationSeconds: number;
  recordingSource?: string;
  recordingStartTime?: string;
}): string {
  const source = params.recordingSource ?? 'unknown';
  const started = normalizeRecordingStartTime(params.recordingStartTime);
  const startedSuffix = started ? ` started=${started}` : '';
  return `Call Recording - ${params.recordingStatus} (${params.durationSeconds}s) [${source}]${startedSuffix}`;
}

export function parseRecordingDurationFromPayloadTitle(title: string | undefined): number {
  return Number(title?.match(/(\d+)\s*s/)?.[1] ?? 0);
}

export function parseRecordingSourceFromPayloadTitle(title: string | undefined): string | undefined {
  return title?.match(/\[([^\]]+)\]/)?.[1];
}

export function parseRecordingStartTimeFromPayloadTitle(title: string | undefined): number {
  const started = title?.match(/started=([^\s\]]+)/)?.[1];
  if (!started) {
    return 0;
  }
  const parsed = Date.parse(started);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/** Update an existing segment (same RecordingSid) when Twilio sends in-progress growth or completed. */
export function shouldUpdateVoiceRecordingSegment(params: {
  existingDurationSeconds: number;
  newDurationSeconds: number;
  recordingStatus: string;
}): boolean {
  const { existingDurationSeconds, newDurationSeconds, recordingStatus } = params;
  if (recordingStatus === 'completed') {
    return newDurationSeconds >= existingDurationSeconds;
  }
  return newDurationSeconds > existingDurationSeconds;
}

/**
 * Append a new Twilio recording segment. Never replaces earlier segments.
 * Skips duplicate RecordingSid or duplicate start+duration pairs.
 */
export function shouldAppendVoiceRecordingSegment(params: {
  existingPayloadIds: string[];
  newRecordingSid: string;
  existingSegments: Array<{ startTimeMs: number; durationSeconds: number }>;
  newStartTimeMs: number;
  newDurationSeconds: number;
}): boolean {
  const newPayloadId = voiceRecordingPayloadId(params.newRecordingSid);
  if (params.existingPayloadIds.includes(newPayloadId)) {
    return false;
  }
  if (params.newStartTimeMs > 0) {
    const duplicate = params.existingSegments.some(
      (segment) =>
        segment.startTimeMs === params.newStartTimeMs && segment.durationSeconds === params.newDurationSeconds
    );
    if (duplicate) {
      return false;
    }
  }
  return true;
}

/**
 * @deprecated Prefer append-only segment storage. Kept for tests and legacy reads.
 * Never replace an earlier-started segment with a later-started one.
 */
export function shouldReplaceVoiceRecordingPayload(params: {
  hasExisting: boolean;
  existingDurationSeconds: number;
  newDurationSeconds: number;
  recordingStatus: string;
  existingRecordingSource?: string;
  newRecordingSource?: string;
  existingStartTimeMs?: number;
  newStartTimeMs?: number;
}): boolean {
  const {
    hasExisting,
    existingDurationSeconds,
    newDurationSeconds,
    recordingStatus,
    existingRecordingSource,
    newRecordingSource,
    existingStartTimeMs = 0,
    newStartTimeMs = 0,
  } = params;
  if (!hasExisting) {
    return true;
  }

  if (existingStartTimeMs > 0 && newStartTimeMs > 0 && newStartTimeMs > existingStartTimeMs) {
    return false;
  }

  const existingFragmented = isFragmentedRecordingSource(existingRecordingSource);
  const newFragmented = isFragmentedRecordingSource(newRecordingSource);

  if (!newFragmented && existingFragmented) {
    return newDurationSeconds > 0 || recordingStatus === 'completed';
  }
  if (newFragmented && !existingFragmented) {
    return false;
  }

  if (recordingStatus !== 'completed') {
    return newDurationSeconds > existingDurationSeconds;
  }
  return newDurationSeconds >= existingDurationSeconds;
}
